import { useEffect, useState } from "react";
import { useKeyStore } from "./store/keyStore";
import { useSessionStore } from "./store/sessionStore";
import { useChannelKeyStore } from "./store/channelKeyStore";
import { useAuthStore } from "./store/authStore";
import { sendChat } from "./lib/chat-send";
import { useDocumentStore } from "./store/documentStore";
import { sendBrainstorm, sendBrainstormMoshPit, reframeBrainstorm, type BrainstormCandidate } from "./lib/brainstorm";
import type { BabyAIMoshPitResult } from "./lib/babyai-client";
import { readJoinParamFromLocation, stripJoinParamFromLocation } from "./lib/session";
import { useSyncLoop } from "./lib/sync-loop";
import { ChannelSidebar } from "./components/ChannelSidebar";
import { SessionToolbar } from "./components/SessionToolbar";
import { ChatThread } from "./components/ChatThread";
import { Composer } from "./components/Composer";
import { ApiKeyModal } from "./components/ApiKeyModal";
import { UnlockChannelModal } from "./components/UnlockChannelModal";
import { SignInGate } from "./components/SignInGate";
import { DocumentPanel } from "./components/DocumentPanel";
import { LibraryView } from "./components/LibraryView";
import { SearchModal } from "./components/SearchModal";
import { WikiView } from "./wiki";

export function App() {
  const authUser = useAuthStore((s) => s.user);
  const { babyai, anthropic, usePublic } = useKeyStore();
  const { channels, activeChannel, createChannel, append } = useSessionStore();
  const channelKeyStore = useChannelKeyStore();
  const active = activeChannel();

  // Display name comes from the Google account — no separate "what
  // should we call you" input. Falls back to "anon" only in the
  // pre-sign-in render window (which the gate prevents from reaching
  // any actual chat surface).
  const userName = authUser?.name ?? "anon";

  useSyncLoop();

  const [keysOpen, setKeysOpen] = useState(false);
  const [unlockOpen, setUnlockOpen] = useState(false);
  const [view, setView] = useState<"chat" | "library" | "wiki">("chat");
  const [wikiInitial, setWikiInitial] = useState<string | undefined>(undefined);
  const [busy, setBusy] = useState(false);
  const [pendingStream, setPendingStream] = useState<string | undefined>(undefined);
  const [pendingDocument, setPendingDocument] = useState<string | undefined>(undefined);
  const [pendingBrainstormCandidates, setPendingBrainstormCandidates] = useState<BrainstormCandidate[] | undefined>(undefined);
  const [pendingMoshPit, setPendingMoshPit] = useState<BabyAIMoshPitResult | undefined>(undefined);
  const [error, setError] = useState<string | null>(null);
  const [dragOverChat, setDragOverChat] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);

  const channelHasUnlockedKeys = !!(active && channelKeyStore.get(active.id));
  const channelLocked = !!(active?.encrypted_keys && !channelHasUnlockedKeys);
  const hasAnyKey = !!(babyai || anthropic || channelHasUnlockedKeys || usePublic);

  // On-mount: import a `?join=…` invite if present. Runs before the
  // empty-channel seed so the invite doesn't get crowded out by a
  // scratch channel.
  useEffect(() => {
    const joined = readJoinParamFromLocation();
    if (joined) {
      useSessionStore.getState().addChannel(joined);
      stripJoinParamFromLocation();
    }
    // Intentional one-shot on mount.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // First-run: open the keys modal if no keys are set.
  useEffect(() => {
    if (!hasAnyKey) setKeysOpen(true);
  }, [hasAnyKey]);

  // Cmd/Ctrl+K opens the cross-channel search. Window-level listener
  // so it works regardless of focus (input, panel, sidebar, etc.).
  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === "k") {
        e.preventDefault();
        setSearchOpen((v) => !v);
      }
    }
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  // First-run: seed an initial channel so the chat surface isn't a void.
  useEffect(() => {
    if (hasAnyKey && channels.length === 0) createChannel("scratch", userName);
  }, [hasAnyKey, channels.length, createChannel, userName]);

  // Switching channels invalidates in-flight UI state — a Mosh Pit picker
  // for channel A makes no sense if we just flipped to B.
  useEffect(() => {
    setPendingStream(undefined);
    setPendingDocument(undefined);
    setPendingBrainstormCandidates(undefined);
    setPendingMoshPit(undefined);
    setError(null);
  }, [active?.id]);

  // Say: post a user message to the channel without invoking the AI.
  // The whole point of the multiplayer substrate — humans chatting,
  // bot summoned on demand.
  function handleSay(text: string) {
    if (!active) return;
    setError(null);
    append({ role: "user", content: text, author: userName });
  }

  // Ask: optionally post a user message, then run the AI. In chat
  // mode this is the normal agent/Mosh-Pit flow. In brainstorm mode
  // it's the whiteboard-rewrite flow — AI updates session.document
  // and posts a one-line ack to the thread.
  async function handleAsk(text: string) {
    if (!active) return;
    setError(null);

    if (text) {
      append({ role: "user", content: text, author: userName });
    }
    setBusy(true);

    try {
      // Re-read after the append — store mutations are sync but the
      // closure-captured `active` predates the append.
      const current = useSessionStore.getState().activeChannel()!;

      if (current.mode === "brainstorm") {
        if (current.brainstorm_mp_enabled) {
          // Mosh-Pit brainstorm: 3 parallel rewrites with 3 organizing
          // strategies. Surface candidates to the panel; user picks
          // which one commits to session.document.
          const out = await sendBrainstormMoshPit({ session: current });
          useSessionStore.getState().recordUsage(current.id, out.usage?.prompt ?? 0, out.usage?.completion ?? 0);
          setPendingBrainstormCandidates(out.candidates);
          setPendingStream(undefined);
          return;
        }
        const out = await sendBrainstorm({
          session: current,
          onDocumentProgress: (snapshot) => setPendingDocument(snapshot),
        });
        useSessionStore.getState().setChannelDocument(current.id, out.document);
        append({ role: "assistant", content: out.ack, model: out.model });
        useSessionStore.getState().recordUsage(current.id, out.usage?.prompt ?? 0, out.usage?.completion ?? 0);
        setPendingDocument(undefined);
        setPendingStream(undefined);
        return;
      }

      const result = await sendChat({
        session: current,
        onToken: (delta) => {
          setPendingStream((prev) => (prev ?? "") + delta);
        },
      });

      if (result.kind === "mosh_pit") {
        setPendingMoshPit(result.result);
        setPendingStream(undefined);
      } else if (result.kind === "agent") {
        append({
          role: "assistant",
          content: result.text,
          model: result.model,
          tool_rounds: result.tool_rounds,
        });
        setPendingStream(undefined);
      } else {
        append({ role: "assistant", content: result.text, model: result.model });
        setPendingStream(undefined);
      }
    } catch (e) {
      const msg = e instanceof Error ? e.message : String(e);
      setError(msg);
      setPendingStream(undefined);
      setPendingDocument(undefined);
      setPendingBrainstormCandidates(undefined);
    } finally {
      setBusy(false);
    }
  }

  function handleMoshPitAccept(provider: string, content: string) {
    append({ role: "assistant", content, picked_provider: provider });
    setPendingMoshPit(undefined);
  }

  async function handleReframe(frameLabel: string, frameTemplate: string) {
    if (!active) return;
    setError(null);
    setBusy(true);
    try {
      const current = useSessionStore.getState().activeChannel()!;
      const out = await reframeBrainstorm({
        session: current,
        targetTemplate: frameTemplate,
        targetLabel: frameLabel,
        onDocumentProgress: (snapshot) => setPendingDocument(snapshot),
      });
      useSessionStore.getState().setChannelDocument(current.id, out.document);
      append({ role: "assistant", content: out.ack, model: out.model });
      useSessionStore.getState().recordUsage(current.id, out.usage?.prompt ?? 0, out.usage?.completion ?? 0);
      setPendingDocument(undefined);
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
      setPendingDocument(undefined);
    } finally {
      setBusy(false);
    }
  }

  async function handleChatDrop(dt: DataTransfer | null) {
    setDragOverChat(false);
    if (!dt || !active) return;
    const docStore = useDocumentStore.getState();
    const sessionStore = useSessionStore.getState();

    // Files first — desktop drag-drops of .txt / .md
    const files = Array.from(dt.files);
    const accepted = [".txt", ".md", ".markdown"];
    for (const f of files) {
      const lower = f.name.toLowerCase();
      if (!accepted.some((e) => lower.endsWith(e))) {
        append({
          role: "system",
          content: `Skipped ${f.name} — only .txt or .md accepted.`,
        });
        continue;
      }
      try {
        const content = await f.text();
        docStore.addDocument(f.name, content);
        sessionStore.attachDocument(active.id, f.name, content, userName);
        append({
          role: "system",
          content: `Attached ${f.name} (${(f.size / 1024).toFixed(1)}KB) — AI sees it on every turn until detached.`,
        });
      } catch (e) {
        append({
          role: "system",
          content: `Couldn't attach ${f.name}: ${e instanceof Error ? e.message : String(e)}`,
        });
      }
    }
    if (files.length > 0) return;

    // URL fallback — drag-drop from a browser tab carries text/uri-list
    // or text/plain. If it looks like a HuggingFace URL, fetch + attach.
    const uri = (dt.getData("text/uri-list") || dt.getData("text/plain") || "").trim();
    if (!uri) return;
    const rawUrl = toHfRawReadmeUrlForChat(uri);
    if (!rawUrl) {
      append({
        role: "system",
        content: `Skipped ${uri.slice(0, 80)} — only HuggingFace model/dataset/space URLs are auto-fetchable.`,
      });
      return;
    }
    try {
      const resp = await fetch(rawUrl);
      if (!resp.ok) throw new Error(`HuggingFace returned ${resp.status} ${resp.statusText}`);
      const content = await resp.text();
      const name = hfNameFromUrlForChat(uri);
      docStore.addDocument(name, content);
      sessionStore.attachDocument(active.id, name, content, userName);
      append({
        role: "system",
        content: `Attached ${name} (${(content.length / 1024).toFixed(1)}KB) — AI sees it on every turn until detached.`,
      });
    } catch (e) {
      append({
        role: "system",
        content: `Couldn't fetch ${uri}: ${e instanceof Error ? e.message : String(e)}`,
      });
    }
  }

  function handleBrainstormCandidateAccept(c: BrainstormCandidate) {
    if (!active) return;
    useSessionStore.getState().setChannelDocument(active.id, c.document);
    append({
      role: "assistant",
      content: `${c.ack} (strategy: ${c.strategy})`,
      picked_provider: c.strategy,
    });
    setPendingBrainstormCandidates(undefined);
  }

  if (!authUser) return <SignInGate />;

  return (
    <div className="app">
      {view === "chat" && <ChannelSidebar />}

      <main className="main">
        <SessionToolbar
          view={view}
          onChangeView={(v) => { setView(v); setWikiInitial(undefined); }}
          onOpenKeys={() => setKeysOpen(true)}
          onOpenWikiPage={(id) => { setWikiInitial(id); setView("wiki"); }}
        />

        {view === "chat" ? (
          <div
            className={`chat-dropzone ${dragOverChat ? "drag-over" : ""}`}
            onDragOver={(e) => {
              if (!active) return;
              // dataTransfer.items is populated during drag; show overlay
              // for files OR string drags (URL drops from a browser tab).
              const hasUseful = Array.from(e.dataTransfer.items).some(
                (i) => i.kind === "file" || i.type === "text/uri-list" || i.type === "text/plain",
              );
              if (hasUseful) {
                e.preventDefault();
                setDragOverChat(true);
              }
            }}
            onDragLeave={(e) => {
              // Only clear when the drag actually leaves the wrapper, not when
              // moving over a child.
              if (e.currentTarget === e.target) setDragOverChat(false);
            }}
            onDrop={(e) => {
              e.preventDefault();
              void handleChatDrop(e.dataTransfer);
            }}
          >
            {dragOverChat && (
              <div className="chat-drop-overlay">
                <div className="chat-drop-msg">drop to attach .txt / .md or a HuggingFace URL</div>
              </div>
            )}
            {active && (active.attached_documents?.length ?? 0) > 0 && (
              <div className="attached-strip">
                <span className="attached-strip-label">attached:</span>
                {active.attached_documents!.map((d) => (
                  <button
                    key={d.id}
                    className="attached-chip"
                    onClick={() => useSessionStore.getState().detachDocument(active.id, d.id)}
                    title={`${(d.bytes / 1024).toFixed(1)}KB · attached by ${d.attached_by} · click to detach`}
                  >
                    {d.name}
                    <span className="attached-chip-x">×</span>
                  </button>
                ))}
              </div>
            )}
            {channelLocked && (
              <div className="banner banner-lock">
                This channel carries encrypted keys for {active!.encrypted_keys!.providers.join(" + ")}.
                {" "}
                <button className="link-btn" onClick={() => setUnlockOpen(true)}>Unlock</button>
                {" "}to use them, or chat with your personal keys.
              </div>
            )}

            {!hasAnyKey && !channelLocked && (
              <div className="banner">
                No keys configured — add a BabyAI token or Anthropic key to start chatting.
              </div>
            )}

            {error && (
              <div className="banner banner-error">
                {error}
              </div>
            )}

            {active ? (
              active.mode === "brainstorm" ? (
                <div className="split">
                  <ChatThread
                    session={active}
                    pendingStream={pendingStream}
                    pendingMoshPit={pendingMoshPit}
                    onMoshPitAccept={handleMoshPitAccept}
                    busy={busy}
                  />
                  <DocumentPanel
                    session={active}
                    pendingDocument={pendingDocument}
                    pendingCandidates={pendingBrainstormCandidates}
                    onAcceptCandidate={handleBrainstormCandidateAccept}
                    onDismissCandidates={() => setPendingBrainstormCandidates(undefined)}
                    onReframe={handleReframe}
                  />
                </div>
              ) : (
                <ChatThread
                  session={active}
                  pendingStream={pendingStream}
                  pendingMoshPit={pendingMoshPit}
                  onMoshPitAccept={handleMoshPitAccept}
                  busy={busy}
                />
              )
            ) : (
              <div className="thread">
                <div className="empty">
                  <h2>BabyAI Playground</h2>
                  <p>Pick a channel on the left, or create one to begin.</p>
                </div>
              </div>
            )}

            <Composer
              onSay={handleSay}
              onAsk={handleAsk}
              disabled={busy || !active || !hasAnyKey || !!pendingMoshPit || !!pendingBrainstormCandidates}
              placeholder={
                pendingBrainstormCandidates ? "Pick a brainstorm strategy on the right to continue." :
                pendingMoshPit ? "Pick a Mosh Pit candidate above to continue." :
                undefined
              }
            />
          </div>
        ) : view === "library" ? (
          <LibraryView />
        ) : (
          <WikiView initialId={wikiInitial} />
        )}
      </main>

      {searchOpen && <SearchModal onClose={() => setSearchOpen(false)} />}
      {keysOpen && <ApiKeyModal onClose={() => setKeysOpen(false)} />}
      {unlockOpen && active && (
        <UnlockChannelModal session={active} onClose={() => setUnlockOpen(false)} />
      )}
    </div>
  );
}

// Tiny duplicates of LibraryView's HF URL helpers. Inlined here to
// avoid a cross-module import that would only carry two functions;
// if a third place ever needs these we'll lift into src/lib/hf.ts.
function toHfRawReadmeUrlForChat(url: string): string | null {
  let parsed: URL;
  try { parsed = new URL(url); } catch { return null; }
  if (parsed.hostname !== "huggingface.co") return null;
  const parts = parsed.pathname.replace(/^\/+|\/+$/g, "").split("/");
  let prefix = "", owner = "", name = "";
  if (parts.length >= 3 && (parts[0] === "datasets" || parts[0] === "spaces")) {
    prefix = `${parts[0]}/`; owner = parts[1]; name = parts[2];
  } else if (parts.length >= 2) {
    owner = parts[0]; name = parts[1];
  } else { return null; }
  if (!owner || !name) return null;
  return `https://huggingface.co/${prefix}${owner}/${name}/raw/main/README.md`;
}

function hfNameFromUrlForChat(url: string): string {
  try {
    const parts = new URL(url).pathname.replace(/^\/+|\/+$/g, "").split("/");
    if (parts.length >= 3 && (parts[0] === "datasets" || parts[0] === "spaces")) {
      return `${parts[0]} · ${parts[1]}/${parts[2]} README.md`;
    }
    if (parts.length >= 2) return `${parts[0]}/${parts[1]} README.md`;
    return "HF README.md";
  } catch { return "HF README.md"; }
}
